import type { Config } from 'tailwindcss'
import plugin from 'tailwindcss/plugin'

const toPX = (values: number[]) =>
  Object.fromEntries(values.map(v => [+v, `${v}px`]))
const object0to100px = toPX(Array.from({ length: 101 }).map((_, i) => +i))

const fallbackSansSerifFonts = [
  '-apple-system',
  'BlinkMacSystemFont',
  'Helvetica',
  'Segoe UI',
  'Arial',
  'sans-serif'
]

const config: Config = {
  content: [
    './src/pages/**/*.{js,ts,jsx,tsx,mdx}',
    './src/components/**/*.{js,ts,jsx,tsx,mdx}',
    './src/layouts/**/*.{js,ts,jsx,tsx,mdx}',
    './src/app/**/*.{js,ts,jsx,tsx,mdx}'
  ],

  corePlugins: {
    container: false
  },

  theme: {
    spacing: object0to100px,

    screens: {
      xxl: { max: '1920px' },
      xl: { max: '1679px' },
      lg: { max: '1280px' },
      md: { max: '1023px' },
      sm: { max: '767px' },
      xs: { max: '639px' },
      xxs: { max: '493px' }
    },

    fontFamily: {
      sans: ['"DIN 2014"', ...fallbackSansSerifFonts],
      pro: ['"DIN Pro"', ...fallbackSansSerifFonts],
      condensed: ['"DIN 2014 Condensed"', ...fallbackSansSerifFonts],
      drugs: ['"TT Drugs"', '"DIN 2014"', ...fallbackSansSerifFonts]
    },

    extend: {
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' }
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' }
        }
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out'
      },

      backgroundImage: {
        'gradient-radial': 'radial-gradient(var(--tw-gradient-stops))',
        'gradient-conic':
          'conic-gradient(from 180deg at 50% 50%, var(--tw-gradient-stops))',
        'gradient-brandBlue':
          'linear-gradient(90deg, #0052FB 0%, #6194FF 100%);'
      }
    },

    borderRadius: {
      ...object0to100px,
      full: '9999px'
    },

    colors: {
      transparent: 'transparent',

      white: '#ffffff',
      black: '#101820',

      deepBlack: '#101820',

      grey: {
        10: '#F5F5F5',
        20: '#E8E9EA',
        30: '#CFCFD0',
        40: '#BBBEC0',
        50: '#A7A8AA',
        60: '#8E9296',
        70: '#5B6770',
        80: '#61666C',
        90: '#2B343C',
        100: '#1D252D'
      },

      beige: {
        10: '#F2F3EE',
        20: '#E9E8E0',
        30: '#E0DDD2',
        40: '#D6D2C4',
        50: '#D0CAB6',
        60: '#CBC1A8',
        70: '#C5B89A'
      },

      bronze: {
        5: '#F8F6F2',
        10: '#EDE7DB',
        20: '#BAA77D',
        30: '#B49E6E',
        40: '#AE955F',
        50: '#A38958',
        60: '#987C50',
        70: '#8C6F48'
      },

      green: {
        10: '#F4F9ED',
        20: '#DEECCB',
        30: '#C6DFA5',
        40: '#ADD17E',
        50: '#97C45B',
        60: '#7FB635',
        70: '#64A70B',
        80: '#568A11',
        90: '#486E17',
        100: '#3B511D'
      },

      orange: {
        10: '#FFF8EF',
        20: '#FFEBCF',
        30: '#FFDEB0',
        40: '#FFD08F',
        50: '#FFC370',
        60: '#FFB54F',
        70: '#FFA62B',
        80: '#CD8829',
        90: '#956727',
        100: '#614825'
      },

      blue: {
        10: '#ECF4FC',
        20: '#CCE2F6',
        30: '#A7CDF0',
        40: '#85B9EA',
        50: '#64A7E4',
        60: '#4092DE',
        70: '#1B7DD8',
        80: '#1D6AB2',
        90: '#205687',
        100: '#23405B'
      },

      red: {
        10: '#FEF1EE',
        20: '#FCD7CF',
        30: '#FAB6A7',
        40: '#F89A85',
        50: '#F67F64',
        60: '#F46444',
        70: '#DD524C',
        80: '#C34022',
        90: '#923723',
        100: '#632F23'
      }
    }
  },

  plugins: [
    require('@tailwindcss/typography'),
    require('tailwindcss-animate'),
    plugin(({ addUtilities }) => {
      const typography = {
        '.text-10': {
          fontSize: '10px',
          lineHeight: '14px'
        },
        '.text-12': {
          fontSize: '12px',
          lineHeight: '16px'
        },
        '.text-14': {
          fontSize: '14px',
          lineHeight: '20px'
        },
        '.text-16': {
          fontSize: '16px',
          lineHeight: '24px'
        },
        '.text-18': {
          fontSize: '18px',
          lineHeight: '24px'
        },
        '.text-20': {
          fontSize: '20px',
          lineHeight: '28px'
        },
        '.text-24': {
          fontSize: '24px',
          lineHeight: '32px'
        },
        '.text-28': {
          fontSize: '28px',
          lineHeight: '36px'
        },
        '.text-32': {
          fontSize: '32px',
          lineHeight: '36px'
        },
        '.text-36': {
          fontSize: '36px',
          lineHeight: '44px'
        },
        '.text-44': {
          fontSize: '44px',
          lineHeight: '52px'
        },
        '.text-52': {
          fontSize: '52px',
          lineHeight: '60px'
        }
      }

      addUtilities(typography)
    })
  ]
}

export default config
